/**
 * <g-tree> - hierarchy / file tree with hover actions and drag-and-drop.
 *
 * Renders nested data as a DaisyUI `menu` file tree. Built for a file / data
 * room view (folders, files, extension icons), but nothing in it is file
 * specific: `icons: false` and it is an org chart or a category tree.
 *
 * Data goes in via JS properties (the golden rule): set `.data` (and the
 * optional `.config`) — never through attributes. Two shapes are read:
 *
 *   nested  [{ id, name, children: [...] }]
 *   flat    [{ id, name, parentId }]          ← set config.fields.parent
 *
 * and every edit is written back in the same shape, into the host's own array
 * and records, BEFORE the event goes out — the bargain <g-task-list> strikes.
 * The host persists; it does not re-apply.
 *
 * SortableJS must be loaded globally (it is, in Gainable apps). Without it the
 * tree still renders and edits; it just cannot be dragged.
 *
 * @attr {boolean} editable   - built-in add / rename / delete, and drag-and-drop
 * @attr {boolean} no-drag    - with `editable`, keep the edits but turn drag off
 * @attr {boolean} selectable - clicking a node selects it (the shared `data-g-selected` tint)
 * @attr {string}  selected   - id of the selected node; reveals its ancestors
 * @attr {string}  expand     - initially open: `none` (default) | `all` | a depth (`1`, `2`…)
 * @attr {string}  size       - menu size: `xs` | `sm` (default) | `md` | `lg`
 * @attr {string}  empty-text - shown when there are no nodes (default 'No items')
 * @attr {string}  empty-icon - Font Awesome class for the empty state
 * @attr {boolean} loading    - presence = skeleton rows
 *
 * @fires node-click  - { id, node, path }
 * @fires node-select - { id, node, path }                 (`selectable` only)
 * @fires node-toggle - { id, node, open }
 * @fires node-action - { action, id, node, path }         cancelable; see below
 * @fires node-added  - { id, parentId, index, label, node, parent, nodes }
 * @fires node-edited - { id, label, previousLabel, node, nodes }
 * @fires node-removed - { id, parentId, node, removed, descendantIds, nodes }
 * @fires node-moved  - { id, node, parentId, oldParentId, index, oldIndex, parent, siblings, nodes }
 *
 * `node-action` fires for EVERY hover action, built-in ones included, and is
 * cancelable: `preventDefault()` on `edit` and the host can open its own modal
 * instead of the inline rename. Actions that are not built in do nothing else.
 */
import { escapeHtml, normalizeAction, renderActionButton } from '../util/actions.js';
import { resolveStatus, statusAttr, ensureStatusStyles } from '../util/status.js';
import { ensureDragStyles, DRAG_CLASSES } from '../util/drag.js';
import { ownsRecordClick } from '../util/record-click.js';
import {
  resolveFields, labelOf, buildTree, sortNodes, descendants, pathTo,
  insertRecord, removeNode, moveNode, fileIconFor, isAncestor,
} from './tree-data.js';

const STYLE_ID = 'g-tree-styles';
const TREE_CSS = `
g-tree{display:block}
g-tree li[role="treeitem"]{outline:none}
g-tree li[role="treeitem"]:focus-visible>[data-g-row]{box-shadow:inset 0 0 0 2px color-mix(in oklab,var(--color-primary) 60%,transparent)}
g-tree li[role="treeitem"]:focus-visible>[data-g-row] [data-g-actions]{opacity:1}
g-tree .menu li>[data-g-row]:active{background-color:color-mix(in oklab,var(--color-primary) 22%,transparent);color:inherit}
g-tree .g-tree-caret{display:inline-flex;align-items:center;justify-content:center;width:1rem;height:1rem;border-radius:.25rem;transition:transform 150ms ease}
g-tree .g-tree-caret>i{font-size:.625rem}
g-tree li[aria-expanded="true"]>[data-g-row] .g-tree-caret{transform:rotate(90deg)}
g-tree [data-g-tree-list]:empty{min-height:1.75rem}
g-tree [data-g-tree-list][data-empty-text=""]:empty{min-height:0}
g-tree [data-g-tree-list][data-empty-text=""]:empty::after{content:none}
g-tree [data-g-tree-list]:empty::after{content:attr(data-empty-text);display:block;padding:.375rem .75rem;font-size:.75rem;opacity:.45;font-style:italic}
g-tree .g-tree-drop-target{box-shadow:inset 0 0 0 2px color-mix(in oklab,var(--color-primary) 55%,transparent);background-color:color-mix(in oklab,var(--color-primary) 8%,transparent)}
g-tree [data-g-row] .input{height:1.5rem;min-height:0}
g-tree.g-tree-dragging ul[role="tree"]{padding-bottom:1.75rem}
g-tree li.g-card-ghost>[data-g-row]{background-color:transparent!important;color:inherit!important}
.g-tree-drag-clone{list-style:none;margin:0;color:var(--color-base-content);background:var(--color-base-100);border-radius:var(--radius-field,.5rem);font-size:.8125rem}
.g-tree-drag-clone>ul,.g-tree-drag-clone [data-g-actions]{display:none!important}
.g-tree-drag-clone>[data-g-row]{display:flex;align-items:center;gap:.5rem;padding:.3rem .75rem}
`;

function ensureTreeStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = TREE_CSS;
  document.head.appendChild(style);
}

const SIZES = new Set(['xs', 'sm', 'md', 'lg']);

// Built-in actions, worded for the data-room case. `config.labels` rewords them
// for any other hierarchy ('Add child', 'Add team', …).
const DEFAULT_LABELS = {
  add: 'New folder',
  edit: 'Rename',
  delete: 'Delete',
  root: 'New folder',
  placeholder: 'Name',
  emptyFolder: 'Empty',
};
// A tree without folder icons is a hierarchy of records — people, categories,
// an outline — where any node can take children and nothing is a "folder".
const HIERARCHY_LABELS = { add: 'Add below', root: 'New', emptyFolder: '' };
const BUILTIN_ICONS = {
  add: 'fa-regular fa-folder-plus',
  edit: 'fa-regular fa-pen',
  delete: 'fa-regular fa-trash',
};

const DEFAULT_ICONS = {
  folder: 'fa-regular fa-folder',
  folderOpen: 'fa-regular fa-folder-open',
  file: 'fa-regular fa-file',
};

// How long a dragged item must hover a closed folder before it springs open.
const SPRING_OPEN_MS = 600;

// Monotonic across every instance — two trees may hold the same object.
let uidSeq = 0;

export class GTree extends HTMLElement {
  constructor() {
    super();
    this._data = [];
    this._config = null;
    this._nodeActions = null;
    this._tree = { roots: [], byId: new Map(), flat: false };
    // User toggles override the `expand` default, in both directions, and
    // outlive each render so a data refresh does not fold the tree back up.
    this._expanded = new Set();
    this._collapsed = new Set();
    this._editing = null;        // { kind: 'rename', id } | { kind: 'add', parentId }
    this._focusId = null;
    this._sortables = [];
    this._isDragging = false;
    this._pendingRender = false;
    this._renderScheduled = false;
    this._uids = new WeakMap();
    this._group = `g-tree-${++uidSeq}`;
    this._pendingDelete = null;
    this._spring = { li: null, timer: 0 };
  }

  static get observedAttributes() {
    return ['editable', 'no-drag', 'selectable', 'selected', 'expand', 'size', 'empty-text', 'empty-icon', 'loading'];
  }

  attributeChangedCallback(name, oldV, newV) {
    if (name === 'selected' && newV && newV !== oldV) this._revealSelected = true;
    this.scheduleRender();
  }

  connectedCallback() {
    ensureStatusStyles();
    ensureDragStyles();
    ensureTreeStyles();
    if (!this._root) { this._root = document.createElement('div'); this.appendChild(this._root); }
    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKeydown);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('focusin', this._onFocusIn);
    this.scheduleRender();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKeydown);
    this.removeEventListener('focusout', this._onFocusOut);
    this.removeEventListener('focusin', this._onFocusIn);
    this._destroySortables();
    this._stopDragTracking();
  }

  // ── Public API ──

  get data() { return this._data; }
  set data(v) { this._data = Array.isArray(v) ? v : []; this.scheduleRender(); }
  // Aliases, so uniform host code (`el.data = …`) and tree-flavoured code both read naturally.
  get nodes() { return this._data; }
  set nodes(v) { this.data = v; }

  get config() { return this._config; }
  set config(c) { this._config = c && typeof c === 'object' ? c : null; this.scheduleRender(); }

  /** `(node, depth) => actions | null` — per-node override of `config.actions`. */
  get nodeActions() { return this._nodeActions; }
  set nodeActions(fn) { this._nodeActions = typeof fn === 'function' ? fn : null; this.scheduleRender(); }

  get selected() { return this.getAttribute('selected'); }
  set selected(id) {
    if (id == null || id === '') this.removeAttribute('selected');
    else this.setAttribute('selected', String(id));
  }

  get editable() { return this.hasAttribute('editable'); }
  set editable(v) { this.toggleAttribute('editable', !!v); }
  get loading() { return this.hasAttribute('loading'); }
  set loading(v) { this.toggleAttribute('loading', !!v); }

  expandAll() {
    this._collapsed.clear();
    for (const n of this._tree.byId.values()) if (n.folder) this._expanded.add(n.id);
    this.scheduleRender();
  }
  collapseAll() {
    this._expanded.clear();
    for (const n of this._tree.byId.values()) if (n.folder) this._collapsed.add(n.id);
    this.scheduleRender();
  }
  /** Opens every ancestor of `id` (and `id` itself when it is a folder). */
  reveal(id) {
    const node = this._tree.byId.get(String(id));
    if (!node) return;
    for (const n of pathTo(node)) if (n.folder) this._open(n);
    this.scheduleRender();
  }
  /** Inline rename, as if the pencil had been clicked. */
  startRename(id) {
    const node = this._tree.byId.get(String(id));
    if (node) this._beginRename(node);
  }
  /** Inline "new" composer under `parentId` (null / omitted = root). */
  startAdd(parentId = null) {
    const parent = parentId == null ? null : this._tree.byId.get(String(parentId));
    if (parentId != null && !parent) return;
    this._beginAdd(parent);
  }

  // ── Config helpers ──

  _cfg() { return this._config || {}; }
  _fields() { return resolveFields(this._config); }
  _labels() { return { ...DEFAULT_LABELS, ...(this._cfg().icons === false ? HIERARCHY_LABELS : {}), ...(this._cfg().labels || {}) }; }
  _sortMode() { return this._cfg().sort || 'manual'; }
  _sorted() { const s = this._sortMode(); return s === 'name' || s === 'folders-first'; }
  _canDrag() { return this.editable && !this.hasAttribute('no-drag') && typeof Sortable !== 'undefined'; }

  _idOf = (record) => {
    const f = this._fields();
    let id = record && record[f.id];
    if (id == null && record) id = record._id;
    if (id == null && record && typeof record === 'object') {
      let uid = this._uids.get(record);
      if (!uid) { uid = `g-tree-u${++uidSeq}`; this._uids.set(record, uid); }
      return uid;
    }
    return id == null ? '' : String(id);
  };

  _rebuild() {
    const cfg = this._cfg();
    this._tree = buildTree(this._data, {
      fields: this._fields(),
      idOf: this._idOf,
      // In a hierarchy every node is a container: it opens, takes an add and
      // takes a drop onto its name. One with no children still draws as a leaf
      // (no caret, no "Empty" hint). Without this, "+" on a childless person
      // opened a composer inside a list that stayed closed, and a drop onto
      // their name became a sibling (QA B03-12, team-tasks 2026-11-02).
      isFolder: cfg.isFolder || (cfg.icons === false ? () => true : undefined),
      folderType: cfg.folderType,
    });
  }

  _defaultOpen(node) {
    const v = (this.getAttribute('expand') || 'none').trim();
    if (v === 'all') return true;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? node.depth < n : false;
  }
  _isOpen(node) {
    if (!node.folder) return false;
    if (this._collapsed.has(node.id)) return false;
    if (this._expanded.has(node.id)) return true;
    // `expand="all"` is about revealing content: opening every empty folder by
    // default would only scatter "Empty" hints down the tree.
    return node.children.length > 0 && this._defaultOpen(node);
  }
  _open(node) { this._collapsed.delete(node.id); this._expanded.add(node.id); }
  _close(node) { this._expanded.delete(node.id); this._collapsed.add(node.id); }

  _iconFor(node, open) {
    const cfg = this._cfg();
    if (cfg.icons === false) return null;
    const f = this._fields();
    const own = f.icon && node.record[f.icon];
    if (typeof own === 'string' && own.startsWith('fa-')) return { icon: own, tone: '' };
    const icons = { ...DEFAULT_ICONS, ...(cfg.icons && typeof cfg.icons === 'object' ? cfg.icons : {}) };
    if (node.folder) return { icon: open ? icons.folderOpen : icons.folder, tone: '' };
    if (cfg.fileIcons !== false) {
      const byExt = fileIconFor(labelOf(node.record, f));
      if (byExt) return byExt;
    }
    return { icon: icons.file, tone: '' };
  }

  /** A folder, or any node of a hierarchy (icons off): it can take children. */
  _canHold(node) {
    return !!node && (node.folder || this._cfg().icons === false);
  }

  _actionsFor(node) {
    let list = null;
    if (this._nodeActions) {
      try { list = this._nodeActions(node.record, node.depth); } catch (err) {
        console.warn('[g-tree] nodeActions threw; falling back to config.actions.', err);
      }
    }
    if (!Array.isArray(list)) {
      const a = this._cfg().actions;
      if (Array.isArray(a)) list = a;
      else if (a && typeof a === 'object') list = (node.folder ? a.folder : a.file) || [];
      else list = this.editable ? (this._canHold(node) ? ['add', 'edit', 'delete'] : ['edit', 'delete']) : [];
    }
    const labels = this._labels();
    return list.map(normalizeAction).filter(Boolean)
      // A file cannot hold children, so "add" under one is meaningless.
      .filter(spec => spec.id !== 'add' || this._canHold(node))
      .map(spec => {
        if (!BUILTIN_ICONS[spec.id]) return spec;
        const raw = list.find(a => (typeof a === 'string' ? a : a && a.id) === spec.id);
        const named = raw && typeof raw === 'object';
        return {
          ...spec,
          label: named && raw.label ? raw.label : labels[spec.id],
          icon: named && raw.icon ? raw.icon : this._builtinIcon(spec.id),
        };
      });
  }

  _builtinIcon(id) {
    return id === 'add' && this._cfg().icons === false ? 'fa-regular fa-plus' : BUILTIN_ICONS[id];
  }

  _meta(record) {
    const m = this._cfg().meta;
    if (!m) return '';
    try {
      const v = typeof m === 'function' ? m(record) : record[m];
      return v == null || v === '' ? '' : String(v);
    } catch { return ''; }
  }

  // ── Render ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      // Never rebuild the DOM out from under an active drag — queue it.
      if (this._isDragging) { this._pendingRender = true; return; }
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    this._rebuild();
    const hadFocus = this.contains(document.activeElement) && !this._pendingDelete;
    const editInput = this._root.querySelector('[data-g-tree-input]');
    // A host refresh mid-typing must not eat the draft — but only the SAME
    // edit gets it back, never the next one.
    const draft = editInput && editInput.dataset.editKey === this._editKey()
      ? { value: editInput.value, start: editInput.selectionStart, end: editInput.selectionEnd } : null;

    this._destroySortables();

    if (this._revealSelected) {
      this._revealSelected = false;
      const sel = this._tree.byId.get(this.selected || '');
      if (sel) for (const n of pathTo(sel).slice(0, -1)) this._open(n);
    }
    // An edit whose target vanished in a refresh has nothing left to edit.
    if (this._editing) {
      const e = this._editing;
      if ((e.kind === 'rename' && !this._tree.byId.has(e.id))
        || (e.kind === 'add' && e.parentId != null && !this._tree.byId.has(e.parentId))) this._editing = null;
    }

    const size = SIZES.has(this.getAttribute('size')) ? this.getAttribute('size') : 'sm';
    if (this.loading) {
      this._root.innerHTML = this._skeleton(size);
      return;
    }

    const roots = sortNodes(this._tree.roots, this._sortMode(), this._fields());
    if (!roots.length && !(this._editing && this._editing.kind === 'add')) {
      this._root.innerHTML = this._empty();
      return;
    }

    // Roving tabindex: exactly one row is in the tab order.
    if (!this._focusId || !this._tree.byId.has(this._focusId)) {
      this._focusId = this._tree.byId.has(this.selected || '') ? this.selected : (roots[0] && roots[0].id) || null;
    }

    const labels = this._labels();
    const rootAdd = this.editable && this._cfg().rootAdd !== false;
    this._root.innerHTML =
      `<ul class="menu menu-${size} w-full p-0" role="tree" data-g-tree-list data-parent-id=""`
      + ` data-empty-text="${escapeHtml(labels.emptyFolder)}"`
      + `>${this._renderList(roots, null)}</ul>`
      + (rootAdd
        ? `<button type="button" class="btn btn-ghost btn-sm w-full justify-start gap-2 font-normal text-base-content/70 mt-1" data-tree-add-root>`
          + `<i class="${escapeHtml(this._builtinIcon('add'))}" aria-hidden="true"></i>${escapeHtml(labels.root)}</button>`
        : '');

    const input = this._root.querySelector('[data-g-tree-input]');
    if (input) {
      if (draft) { input.value = draft.value; try { input.setSelectionRange(draft.start, draft.end); } catch { /* */ } }
      input.focus();
      if (!draft) input.select();
    } else if (hadFocus && this._focusId) {
      const li = this._liFor(this._focusId);
      if (li) li.focus();
    }

    if (this._canDrag()) this._initSortables();
  }

  _editKey() {
    const e = this._editing;
    return e ? `${e.kind}:${e.kind === 'rename' ? e.id : (e.parentId ?? '')}` : '';
  }

  _renderList(nodes, parent) {
    let html = '';
    for (const node of nodes) html += this._renderNode(node);
    const e = this._editing;
    if (e && e.kind === 'add' && (e.parentId ?? null) === (parent ? parent.id : null)) {
      html += this._renderComposer(parent);
    }
    return html;
  }

  _renderNode(node) {
    const f = this._fields();
    const open = this._isOpen(node);
    const selected = this.hasAttribute('selectable') && this.selected === node.id;
    const label = labelOf(node.record, f);
    const icon = this._iconFor(node, open);
    const status = resolveStatus(node.record, node.depth, this._cfg().status);
    const renaming = this._editing && this._editing.kind === 'rename' && this._editing.id === node.id;
    const kids = node.folder ? sortNodes(node.children, this._sortMode(), f) : [];
    const hasKids = kids.length > 0 || (this._editing && this._editing.kind === 'add' && this._editing.parentId === node.id);

    const caret = node.folder && hasKids
      ? `<span class="g-tree-caret" aria-hidden="true"><i class="fa-regular fa-chevron-right"></i></span>`
      : `<span class="g-tree-caret" aria-hidden="true"></span>`;
    // Chevron AND folder icon fold; the label does not.
    const toggle = node.folder ? ' data-g-tree-toggle' : '';
    const iconHtml = icon
      ? `<i class="${escapeHtml(icon.icon)} ${icon.tone || ''} fa-fw" aria-hidden="true"></i>`
      : '';
    const labelHtml = renaming
      ? `<input type="text" class="input input-xs input-bordered w-full min-w-0" data-g-tree-input data-edit-key="${escapeHtml(this._editKey())}" value="${escapeHtml(label)}" aria-label="${escapeHtml(this._labels().edit)}">`
      : `<span class="truncate min-w-0" data-g-tree-label title="${escapeHtml(label)}">${escapeHtml(label)}</span>`;

    const meta = this._meta(node.record);
    const actions = renaming ? [] : this._actionsFor(node);
    const buttons = actions.map(spec => renderActionButton(spec, {
      attr: 'data-tree-action', className: 'btn btn-ghost btn-xs btn-square h-6 w-6 min-h-0', tinted: true,
    })).join('');
    // A fixed-width column, so every row's meta starts at the same x whatever
    // its length — right-aligned free text read as ragged (QA B03-14).
    const trail = (meta ? `<span class="text-xs text-base-content/50 whitespace-nowrap truncate w-48 max-w-[40vw] text-start" title="${escapeHtml(meta)}">${escapeHtml(meta)}</span>` : '')
      + (buttons ? `<span class="inline-flex items-center" data-g-actions>${buttons}</span>` : '');

    const aria = ` role="treeitem" aria-level="${node.depth + 1}"`
      + (node.folder ? ` aria-expanded="${open}"` : '')
      + (this.hasAttribute('selectable') ? ` aria-selected="${selected}"` : '')
      + ` tabindex="${this._focusId === node.id ? 0 : -1}"`;

    let html = `<li data-node-id="${escapeHtml(node.id)}"${aria}>`
      + `<div data-g-row data-g-tree-row${selected ? ' data-g-selected' : ''}${statusAttr(status)}>`
      + `<span class="inline-flex items-center gap-1"${toggle}>${caret}${iconHtml}</span>`
      + labelHtml
      + `<span class="inline-flex items-center gap-2">${trail}</span>`
      + `</div>`;
    const addingHere = !!(this._editing && this._editing.kind === 'add' && this._editing.parentId === node.id);
    if (node.folder || addingHere) {
      // Rendered even when closed, and even when empty: an open-but-empty folder
      // must be a drop target, and a closed one must be ready to spring open
      // under a drag without a re-render.
      html += `<ul role="group" data-g-tree-list data-parent-id="${escapeHtml(node.id)}"`
        + ` data-empty-text="${escapeHtml(this._labels().emptyFolder)}"`
        + ` class="menu-dropdown${open ? ' menu-dropdown-show' : ''}">`
        + this._renderList(kids, node)
        + `</ul>`;
    }
    return html + `</li>`;
  }

  _renderComposer(parent) {
    const icons = { ...DEFAULT_ICONS, ...(this._cfg().icons && typeof this._cfg().icons === 'object' ? this._cfg().icons : {}) };
    const icon = this._cfg().icons === false ? '' : `<i class="${escapeHtml(icons.folder)} fa-fw" aria-hidden="true"></i>`;
    return `<li data-g-tree-composer data-parent-id="${parent ? escapeHtml(parent.id) : ''}">`
      + `<div data-g-row><span class="inline-flex items-center gap-1"><span class="g-tree-caret"></span>${icon}</span>`
      + `<input type="text" class="input input-xs input-bordered w-full min-w-0" data-g-tree-input data-edit-key="${escapeHtml(this._editKey())}"`
      + ` placeholder="${escapeHtml(this._labels().placeholder)}" aria-label="${escapeHtml(this._labels().add)}">`
      + `<span></span></div></li>`;
  }

  _skeleton(size) {
    const rows = [[0, 'w-32'], [1, 'w-40'], [1, 'w-28'], [2, 'w-36'], [0, 'w-24'], [1, 'w-44']];
    return `<ul class="menu menu-${size} w-full p-0" aria-busy="true">`
      + rows.map(([d, w]) => `<li class="pointer-events-none"><div style="padding-inline-start:${0.75 + d * 1.5}rem">`
        + `<span class="skeleton h-4 w-4 shrink-0"></span><span class="skeleton h-3 ${w}"></span><span></span></div></li>`).join('')
      + `</ul>`;
  }

  _empty() {
    const raw = this.getAttribute('empty-icon');
    const icon = raw && /^[\w\s-]+$/.test(raw) ? raw : 'fa-regular fa-folder-open';
    const text = this.getAttribute('empty-text') || 'No items';
    const add = this.editable && this._cfg().rootAdd !== false
      ? `<button type="button" class="btn btn-sm btn-ghost gap-2 mt-1" data-tree-add-root><i class="${escapeHtml(this._builtinIcon('add'))}" aria-hidden="true"></i>${escapeHtml(this._labels().root)}</button>`
      : '';
    return `<div class="flex flex-col items-center justify-center gap-2 py-8 text-base-content/50">`
      + `<i class="${escapeHtml(icon)} text-2xl" aria-hidden="true"></i>`
      + `<span class="text-sm">${escapeHtml(text)}</span>${add}</div>`;
  }

  // ── Events ──

  _emit(name, detail, cancelable = false) {
    const ev = new CustomEvent(name, { detail, bubbles: true, composed: true, cancelable });
    this.dispatchEvent(ev);
    return ev;
  }

  _liFor(id) {
    return [...this._root.querySelectorAll('li[data-node-id]')].find(li => li.dataset.nodeId === id) || null;
  }
  _nodeOf(el) {
    const li = el && el.closest && el.closest('li[data-node-id]');
    return li && this.contains(li) ? this._tree.byId.get(li.dataset.nodeId) || null : null;
  }
  _pathIds(node) { return pathTo(node).map(n => n.id); }

  _onClick = (e) => {
    if (this._isDragging) return;
    const t = e.target;
    if (!(t instanceof Element) || !this._root.contains(t)) return;

    const actionBtn = t.closest('[data-tree-action]');
    if (actionBtn) {
      const node = this._nodeOf(actionBtn);
      if (node) this._runAction(actionBtn.getAttribute('data-tree-action'), node);
      return;
    }
    if (t.closest('[data-tree-add-root]')) { this._beginAdd(null); return; }
    if (t.closest('[data-g-tree-input], [data-g-tree-composer]')) return;

    const node = this._nodeOf(t);
    if (!node) return;
    this._focusId = node.id;

    // The chevron / folder icon only folds; it never selects or opens the record.
    if (t.closest('[data-g-tree-toggle]')) {
      if (node.children.length || this._isOpen(node)) this._toggle(node);
      return;
    }

    const row = t.closest('[data-g-tree-row]');
    if (!row || ownsRecordClick(e, row)) return;
    this._activate(node);
  };

  /** A click (or Enter) on a row: select, announce. Folding is the chevron's / icon's job. */
  _activate(node) {
    if (this.hasAttribute('selectable') && this.selected !== node.id) {
      this.selected = node.id;
      this._emit('node-select', { id: node.id, node: node.record, path: this._pathIds(node) });
    }
    this._emit('node-click', { id: node.id, node: node.record, path: this._pathIds(node) });
  }

  _toggle(node, open = !this._isOpen(node)) {
    if (!node.folder || open === this._isOpen(node)) return;
    if (open) this._open(node); else this._close(node);
    this.scheduleRender();
    this._emit('node-toggle', { id: node.id, node: node.record, open });
  }

  _runAction(action, node) {
    const ev = this._emit('node-action', { action, id: node.id, node: node.record, path: this._pathIds(node) }, true);
    if (ev.defaultPrevented || !this.editable) return;
    if (action === 'add' && this._canHold(node)) this._beginAdd(node);
    else if (action === 'edit') this._beginRename(node);
    else if (action === 'delete') this._requestDelete(node);
  }

  _onFocusIn = (e) => {
    const li = e.target instanceof Element && e.target.matches('li[data-node-id]') ? e.target : null;
    if (!li) return;
    this._focusId = li.dataset.nodeId;
    for (const other of this._root.querySelectorAll('li[data-node-id][tabindex="0"]')) {
      if (other !== li) other.tabIndex = -1;
    }
    li.tabIndex = 0;
  };

  // Leaving an inline field commits it — same as a text field anywhere else.
  // `focusout` rather than `blur`: blur does not bubble.
  //
  // Deferred a tick: committing re-renders, and re-rendering synchronously here
  // would replace the very button the user is mid-way through clicking (the
  // pencil on another row, say), so the click would never land.
  _onFocusOut = (e) => {
    const input = e.target;
    if (!(input instanceof Element) || !input.matches('[data-g-tree-input]')) return;
    const session = this._editing;
    if (!session) return;
    setTimeout(() => {
      // Disconnected = a re-render swapped this input for a fresh one that
      // carries the draft (a host refresh mid-typing). Not a commit.
      if (this._editing !== session || !input.isConnected || document.activeElement === input) return;
      this._commitEdit(input.value);
    }, 0);
  };

  /** Commits whatever is in an open inline field, without re-rendering. */
  _flushEdit() {
    const e = this._editing;
    if (!e) return;
    const input = this._root.querySelector('[data-g-tree-input]');
    this._editing = null;
    const text = String(input ? input.value : '').trim();
    if (e.kind === 'rename') this._rename(e.id, text);
    else if (text) this._add(e.parentId, text);
  }

  _onKeydown = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-g-tree-input]')) {
      if (e.key === 'Enter') { e.preventDefault(); this._commitEdit(t.value, true); }
      else if (e.key === 'Escape') { e.preventDefault(); this._cancelEdit(); }
      return;
    }
    if (!t.matches('li[data-node-id]')) return;
    const node = this._tree.byId.get(t.dataset.nodeId);
    if (!node) return;

    const visible = () => [...this._root.querySelectorAll('li[data-node-id]')]
      .filter(li => !li.parentElement.closest('.menu-dropdown:not(.menu-dropdown-show)'));
    const focus = (li) => { if (li) { e.preventDefault(); li.focus(); } };

    switch (e.key) {
      case 'ArrowDown': { const v = visible(); focus(v[v.indexOf(t) + 1]); break; }
      case 'ArrowUp': { const v = visible(); focus(v[v.indexOf(t) - 1]); break; }
      case 'Home': focus(visible()[0]); break;
      case 'End': { const v = visible(); focus(v[v.length - 1]); break; }
      case 'ArrowRight':
        e.preventDefault();
        if (node.folder && !this._isOpen(node)) this._toggle(node, true);
        else if (node.folder) focus(t.querySelector(':scope > ul > li[data-node-id]'));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (node.folder && this._isOpen(node)) this._toggle(node, false);
        else focus(t.parentElement.closest('li[data-node-id]'));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._activate(node);
        break;
      case 'F2':
        if (this._actionsFor(node).some(a => a.id === 'edit')) { e.preventDefault(); this._runAction('edit', node); }
        break;
      case 'Delete':
        if (this._actionsFor(node).some(a => a.id === 'delete')) { e.preventDefault(); this._runAction('delete', node); }
        break;
      default:
    }
  };

  // ── Inline edits ──

  _beginRename(node) {
    this._flushEdit();
    node = this._tree.byId.get(node.id) || node;
    this._editing = { kind: 'rename', id: node.id };
    this._focusId = node.id;
    this._render();
  }

  _beginAdd(parent) {
    this._flushEdit();
    if (parent) parent = this._tree.byId.get(parent.id) || parent;
    if (parent && !this._canHold(parent)) return;
    if (parent) this._open(parent);
    this._editing = { kind: 'add', parentId: parent ? parent.id : null };
    this._render();
  }

  _cancelEdit() {
    const e = this._editing;
    this._editing = null;
    if (e && e.kind === 'rename') this._focusId = e.id;
    else if (e && e.parentId) this._focusId = e.parentId;
    this._render();
    const li = this._focusId && this._liFor(this._focusId);
    if (li) li.focus();
  }

  _commitEdit(raw, refocus = false) {
    const e = this._editing;
    if (!e) return;
    // Cleared first: the re-render below removes the input, which fires
    // focusout, which would otherwise commit a second time.
    this._editing = null;
    const text = String(raw ?? '').trim();
    if (e.kind === 'rename') this._rename(e.id, text);
    else if (text) this._add(e.parentId, text);
    this._render();
    if (refocus) {
      const li = this._focusId && this._liFor(this._focusId);
      if (li) li.focus();
    }
  }

  _labelKey(record) {
    const f = this._fields();
    if (f.label) return f.label;
    for (const k of ['name', 'label', 'title']) if (record[k] != null) return k;
    return 'name';
  }

  _rename(id, text) {
    const node = this._tree.byId.get(id);
    if (!node) return;
    this._focusId = id;
    const key = this._labelKey(node.record);
    const previousLabel = node.record[key];
    // Opening a field and closing it unchanged (or blank) is not an edit.
    if (!text || String(previousLabel ?? '') === text) return;
    node.record[key] = text;
    this._emit('node-edited', { id, label: text, previousLabel, node: node.record, nodes: this._data });
  }

  _add(parentId, text) {
    const f = this._fields();
    const parent = parentId == null ? null : this._tree.byId.get(parentId);
    if (parentId != null && !parent) return;
    const record = { [f.label || 'name']: text };
    // A new node is a folder: files arrive through the host's own upload flow.
    // Marked the way this data marks folders, so it stays one after a reload.
    const hasType = f.type && [...this._tree.byId.values()].some(n => n.record[f.type] != null);
    if (hasType) record[f.type] = this._cfg().folderType ?? 'folder';
    if (!this._tree.flat) record[f.children] = [];
    const cfg = this._cfg();
    if (typeof cfg.newRecord === 'function') {
      try { Object.assign(record, cfg.newRecord(parent ? parent.record : null, text) || {}); } catch (err) {
        console.warn('[g-tree] config.newRecord threw; using the default record.', err);
      }
    }
    insertRecord(this._data, this._tree, record, parent, f);
    if (parent) this._open(parent);
    this._rebuild();
    const id = this._idOf(record);
    this._focusId = id;
    const siblings = parent ? (this._tree.byId.get(parent.id) || parent).children : this._tree.roots;
    this._emit('node-added', {
      id, parentId: parent ? parent.id : null, index: siblings.findIndex(n => n.record === record),
      label: text, node: record, parent: parent ? parent.record : null, nodes: this._data,
    });
  }

  // ── Delete ──

  _requestDelete(node) {
    const mode = this._cfg().confirmDelete || 'folders';
    const count = descendants(node).length;
    const ask = mode === 'always' || (mode !== 'never' && count > 0);
    if (!ask) { this._remove(node); return; }
    this._confirmDelete(node, count);
  }

  /**
   * In-page on purpose: a native confirm() blocks the page, and any automation
   * driving it. A DaisyUI modal, so the host theme styles it for free.
   */
  _confirmDelete(node, count) {
    this._closeDialog();
    const label = labelOf(node.record, this._fields());
    const dlg = document.createElement('dialog');
    dlg.className = 'modal';
    dlg.setAttribute('data-g-tree-dialog', '');
    dlg.innerHTML = `<div class="modal-box max-w-sm" role="alertdialog">`
      + `<h3 class="text-base font-semibold">Delete &ldquo;${escapeHtml(label)}&rdquo;?</h3>`
      + `<p class="text-sm text-base-content/70 mt-2">${count
        ? (this._cfg().icons === false
          ? `This also deletes the ${count} below it.`
          : `This also deletes the ${count} item${count === 1 ? '' : 's'} inside it.`)
        : 'This can\'t be undone.'}</p>`
      + `<div class="modal-action mt-4">`
      + `<button type="button" class="btn btn-ghost btn-sm" data-g-tree-cancel>Cancel</button>`
      + `<button type="button" class="btn btn-error btn-sm" data-g-tree-confirm>Delete</button>`
      + `</div></div><form method="dialog" class="modal-backdrop"><button>close</button></form>`;
    this._pendingDelete = node.id;
    dlg.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-g-tree-confirm]')) {
        const target = this._tree.byId.get(this._pendingDelete);
        this._closeDialog();
        if (target) { this._remove(target); this._render(); }
      } else if (e.target.closest('[data-g-tree-cancel]')) {
        this._closeDialog();
      }
    });
    dlg.addEventListener('close', () => this._closeDialog());
    this.appendChild(dlg);
    if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
    const confirm = dlg.querySelector('[data-g-tree-confirm]');
    if (confirm) confirm.focus();
  }

  _closeDialog() {
    const dlg = this.querySelector(':scope > dialog[data-g-tree-dialog]');
    const id = this._pendingDelete;
    this._pendingDelete = null;
    if (dlg) { if (dlg.open) dlg.close(); dlg.remove(); }
    const li = id && this._liFor(id);
    if (li) li.focus();
  }

  _remove(node) {
    const f = this._fields();
    const parentId = node.parent ? node.parent.id : null;
    const descendantIds = descendants(node).map(n => n.id);
    // Focus lands somewhere that still exists: the next sibling, else the
    // previous one, else the parent.
    const sibs = sortNodes(node.parent ? node.parent.children : this._tree.roots, this._sortMode(), f);
    const i = sibs.indexOf(node);
    const next = sibs[i + 1] || sibs[i - 1] || node.parent;
    this._focusId = next ? next.id : null;

    const removed = removeNode(this._data, this._tree, node, f);
    if (this.selected && (this.selected === node.id || descendantIds.includes(this.selected))) {
      this.removeAttribute('selected');
    }
    this._rebuild();
    this.scheduleRender();
    this._emit('node-removed', {
      id: node.id, parentId, node: node.record, removed, descendantIds, nodes: this._data,
    });
  }

  // ── Drag and drop (SortableJS, one instance per list) ──

  _initSortables() {
    const sorted = this._sorted();
    for (const ul of this._root.querySelectorAll('ul[data-g-tree-list]')) {
      this._sortables.push(new Sortable(ul, {
        group: this._group,
        // Under a name sort, order within a folder is the sort's to decide;
        // a drag can still move an item INTO another folder.
        sort: !sorted,
        // Pointer-driven rather than native HTML5 DnD — the same choice, and
        // the same reasons, as <g-task-list>: it behaves identically everywhere
        // and it is the only path an automated test can drive.
        forceFallback: true,
        fallbackOnBody: true,
        fallbackClass: 'g-tree-drag-clone',
        fallbackTolerance: 3,
        // Inverted swap zone: only a row's outer quarters reorder; its middle
        // half does nothing, which is what leaves a folder row's middle free to
        // mean "drop into this folder" (see _onDragPointer). In the default
        // mode the placeholder chases the pointer, so a folder row slides away
        // the instant it is reached and can never be hovered.
        invertSwap: true,
        invertedSwapThreshold: 0.5,
        emptyInsertThreshold: 8,
        animation: 150,
        draggable: 'li[data-node-id]',
        handle: '[data-g-tree-row]',
        filter: (evt) => {
          const el = evt.target;
          return !!(el && el.closest && el.closest('button, a, input, textarea, select, label, [data-g-tree-toggle]'));
        },
        preventOnFilter: false,
        ...DRAG_CLASSES,
        onChoose: () => { this._isDragging = true; },
        onUnchoose: () => this._afterDrag(),
        onStart: () => this._startDragTracking(),
        onMove: (evt) => this._onSortableMove(evt),
        onEnd: (evt) => {
          const into = this._spring.li;
          this._stopDragTracking();
          this._handleDrop(evt, into);
          this._afterDrag();
        },
      }));
    }
  }

  _afterDrag() {
    this._isDragging = false;
    if (this._pendingRender) { this._pendingRender = false; this.scheduleRender(); }
  }

  _destroySortables() {
    for (const s of this._sortables) { try { s.destroy(); } catch { /* already gone */ } }
    this._sortables = [];
  }

  _canDrop(dragged, toList) {
    const node = this._nodeOf(dragged);
    if (!node) return false;
    const pid = toList && toList.dataset.parentId;
    const parent = pid ? this._tree.byId.get(pid) : null;
    if (pid && (!parent || !this._canHold(parent) || isAncestor(node, parent))) return false;
    const can = this._cfg().canMove;
    if (typeof can === 'function') {
      try { return can(node.record, parent ? parent.record : null) !== false; } catch { return false; }
    }
    return true;
  }

  /**
   * @param {Object} evt   Sortable's onEnd event
   * @param {?Element} into  folder row the pointer was over at release (see
   *                         _onDragPointer) — wins over the list position
   */
  _handleDrop(evt, into) {
    const node = this._nodeOf(evt.item);
    if (!node) return;
    const intoNode = into && this._tree.byId.get(into.dataset.nodeId);
    let parent;
    let index;
    if (intoNode) {
      // Dropped ON a folder: it joins that folder, at the end.
      parent = intoNode;
      index = Infinity;
      if (node.parent === parent) { this.scheduleRender(); return; }
      if (!this._canDrop(evt.item, into.querySelector(':scope > ul'))) { this.scheduleRender(); return; }
    } else {
      const pid = evt.to.dataset.parentId;
      parent = pid ? this._tree.byId.get(pid) : null;
      if (evt.to === evt.from && evt.newDraggableIndex === evt.oldDraggableIndex) return;
      // Under a name sort the drop position means nothing; the item joins the folder.
      index = this._sorted() ? Infinity : evt.newDraggableIndex;
    }

    const f = this._fields();
    const oldParent = node.parent;
    const oldIndex = (oldParent ? oldParent.children : this._tree.roots).indexOf(node);
    if (!moveNode(this._data, this._tree, node, parent, index, f)) {
      this.scheduleRender();
      return;
    }
    if (parent) this._open(parent);
    this._focusId = node.id;
    this._rebuild();
    const moved = this._tree.byId.get(node.id);
    const siblings = moved ? (moved.parent ? moved.parent.children : this._tree.roots) : [];
    // Always re-render: a drop onto a folder row left Sortable's ghost wherever
    // it last was, and the model, not the DOM, is the truth now.
    this.scheduleRender();
    this._emit('node-moved', {
      id: node.id,
      node: node.record,
      parentId: parent ? parent.id : null,
      oldParentId: oldParent ? oldParent.id : null,
      index: siblings.findIndex(n => n.record === node.record),
      oldIndex,
      parent: parent ? parent.record : null,
      siblings: siblings.map(n => n.record),
      nodes: this._data,
    });
  }

  // ── Drop onto a folder ──
  //
  // A row's top and bottom edges reorder, as Sortable does by itself. Its
  // MIDDLE, on a folder, means "into this folder": Sortable is told not to
  // move there (see _onSortableMove), the row lights up, a release drops the
  // item inside, and holding still springs a closed folder open so it can be
  // placed at an exact position among the contents.
  //
  // Without the middle band, Sortable slides its placeholder under the pointer
  // the moment it reaches a folder row, so the folder can never be hovered,
  // and a collapsed folder could never be dropped into at all.

  _startDragTracking() {
    this._isDragging = true;
    // A strip under the last row while dragging: the only place a drop can mean
    // "end of the top level" once the last root item is an open folder.
    this.classList.add('g-tree-dragging');
    document.addEventListener('pointermove', this._onDragPointer, true);
    document.addEventListener('mousemove', this._onDragPointer, true);
    document.addEventListener('touchmove', this._onDragPointer, true);
  }

  _stopDragTracking() {
    this.classList.remove('g-tree-dragging');
    document.removeEventListener('pointermove', this._onDragPointer, true);
    document.removeEventListener('mousemove', this._onDragPointer, true);
    document.removeEventListener('touchmove', this._onDragPointer, true);
    this._setDropTarget(null);
  }

  // Capture phase on document: runs before Sortable's own move handler, so the
  // target is current by the time Sortable asks _onSortableMove.
  _onDragPointer = (e) => {
    const p = e.touches ? e.touches[0] : e;
    if (!p) return;
    const hit = document.elementFromPoint(p.clientX, p.clientY);
    const row = hit && hit.closest && hit.closest('[data-g-tree-row]');
    const li = row && this._root.contains(row) ? row.parentElement : null;
    const node = li && this._tree.byId.get(li.dataset.nodeId);
    const ghost = this._root.querySelector('li.' + DRAG_CLASSES.ghostClass);
    let target = null;
    if (node && node.folder && !(ghost && ghost.contains(li))) {
      const r = row.getBoundingClientRect();
      const y = (p.clientY - r.top) / (r.height || 1);
      const ul = li.querySelector(':scope > ul');
      if (y > 0.25 && y < 0.75 && ghost && this._canDrop(ghost, ul)) target = li;
    }
    this._setDropTarget(target);
  };

  _onSortableMove(evt) {
    const t = this._spring.li;
    if (t && (evt.related === t || (evt.related && t.contains(evt.related)))) return false;
    return this._canDrop(evt.dragged, evt.to);
  }

  _setDropTarget(li) {
    const s = this._spring;
    if (s.li === li) return;
    if (s.li) s.li.firstElementChild?.classList.remove('g-tree-drop-target');
    clearTimeout(s.timer);
    s.li = li;
    s.timer = 0;
    if (!li) return;
    li.firstElementChild?.classList.add('g-tree-drop-target');
    const node = this._tree.byId.get(li.dataset.nodeId);
    if (!node || this._isOpen(node)) return;
    s.timer = setTimeout(() => {
      if (!li.isConnected) return;
      this._open(node);
      li.setAttribute('aria-expanded', 'true');
      const ul = li.querySelector(':scope > ul');
      if (ul) ul.classList.add('menu-dropdown-show');
      const icon = this._iconFor(node, true);
      const glyph = li.querySelector(':scope > [data-g-row] > span:first-child > i');
      if (icon && glyph) glyph.className = `${icon.icon} ${icon.tone || ''} fa-fw`;
    }, SPRING_OPEN_MS);
  }
}

if (!customElements.get('g-tree')) {
  customElements.define('g-tree', GTree);
}
